import { FC, PropsWithChildren, useCallback } from 'react';
import { Button, Flex, Input } from 'antd';
import * as Icon from 'react-feather';
import styled from 'styled-components';
import { AlgorithmFilter } from '../query/useGetAlgorithmsQuery';
import { AlgorithmTableViewRadio } from './AlgorithmTableViewRadio';
import { AlgorithmsTableActionsDropdown } from './AlgorithmsTableActionsDropdown';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmsTableActions.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithms-table-actions',
);
addTranslations(translations);

export interface AlgorithmsTableActionsProps extends PropsWithChildren {
  onChangeView?: (isAlgorithmView: boolean, isSummaryView: boolean) => void;
  filter?: AlgorithmFilter;
  onFilter?: (filterState: AlgorithmFilter) => void;
  isFilterOpen?: boolean;
  onToggleFilterOpen?: () => void;
  onClickClearFilters?: () => void;
  onClickDeleteSelected?: () => void;
  onClickTestRunSelected?: () => void;
  onClickCheckBrokenReferencesSelected?: () => void;
  onClickDisplayGraphically?: () => void;
  isDisplayGraphicallyDisabled?: boolean;
}

export const AlgorithmsTableActions: FC<AlgorithmsTableActionsProps> = (
  props,
) => {
  const {
    onChangeView,
    filter,
    onFilter,
    isFilterOpen,
    onToggleFilterOpen,
    onClickDeleteSelected,
    onClickTestRunSelected,
    onClickCheckBrokenReferencesSelected,
    onClickDisplayGraphically,
    isDisplayGraphicallyDisabled,
  } = props;
  const { t } = useTranslation();

  const handleSearch = useCallback(
    (search: string) => onFilter?.({ ...filter, search: search || undefined }),
    [onFilter, filter],
  );

  return (
    <ActionsWrapper gap="middle" justify="space-between">
      <Flex gap="middle">
        <StyledAlgorithmTableViewRadio onChangeView={onChangeView} />
        {(!!onClickDeleteSelected ||
          !!onClickTestRunSelected ||
          !!onClickCheckBrokenReferencesSelected) && (
          <AlgorithmsTableActionsDropdown
            onClickDeleteSelected={onClickDeleteSelected}
            onClickTestRunSelected={onClickTestRunSelected}
            onClickCheckBrokenReferencesSelected={
              onClickCheckBrokenReferencesSelected
            }
            onClickDisplayGraphically={onClickDisplayGraphically}
            isDisplayGraphicallyDisabled={isDisplayGraphicallyDisabled}
          />
        )}
      </Flex>
      <Flex gap="middle">
        <Search
          id="algorithms-search"
          placeholder={t('search.placeholder')}
          allowClear
          onSearch={handleSearch}
          defaultValue={filter?.search}
        />
        <Button
          id="toggle-algorithm-directory-filter"
          type="primary"
          onClick={onToggleFilterOpen}
        >
          <Flex gap="small" align="center">
            <Icon.Filter size={14} />
            {t(isFilterOpen ? 'button.close-filter' : 'button.open-filter')}
          </Flex>
        </Button>
      </Flex>
    </ActionsWrapper>
  );
};

const ActionsWrapper = styled(Flex)`
  align-self: stretch;
  flex-grow: 1;
  width: 100%;
  left: inherit;
  right: inherit;
`;

const StyledAlgorithmTableViewRadio = styled(AlgorithmTableViewRadio)`
  position: sticky;
  left: inherit;
`;

const Search = styled(Input.Search)`
  width: auto;
  position: sticky;
  right: inherit;
`;
